import {
	Button,
	Grid,
	Group,
	Modal,
	NumberInput,
	Space,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCancel } from '@tabler/icons-react';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { FormButtons } from '../../../components/form/FormButtons';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { useEditProductCostForm } from '../../products-cost/hooks/useEditProductCostForm';
import type { EditProductCostDto } from '../../products-cost/types/EditProductCostDto';
import type { ProductCostDto } from '../types/ProductCostDto';
import { Title } from '../../../components/Title';

interface EditMinMaxStockModalProps {
	close: () => void;
	opened: boolean;
	updateTableValue: Dispatch<SetStateAction<ProductCostDto>>;
	editProductCost: EditProductCostDto;
}
export function EditProductCostModal({
	close,
	opened,
	updateTableValue,
	editProductCost,
}: EditMinMaxStockModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useEditProductCostForm(editProductCost);

	const editStockLevelMutation = mutate<EditProductCostDto, ProductCostDto>(
		`${organisation}/product-cost/edit`,
		inputs,
	);

	const cancelClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const successClose = () => {
		form.setInitialValues(inputs);
		cancelClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={cancelClose}
				title={<Title label={'Edit Product Cost'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
			>
				{/* <LoadingOverlay visible={newOrganisationMutation.isLoading} /> */}
				<Modal
					opened={showSuccessModal}
					onClose={successClose}
					title={<Title label={'Product Cost Edited!'} />}
					centered
				>
					<Text>
						Product cost has been successfully edited. Click Ok to continue...
					</Text>
					<Group justify={'flex-end'}>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCancel {...defaultIconStyle} />}
							onClick={() => successClose()}
						>
							Ok
						</Button>
					</Group>
				</Modal>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editStockLevelMutation.mutateAsync().then((response) => {
							if (response.isValid) {
								updateTableValue(response.value);
								openSuccessModal();
							}
						});
					})}
				>
					<Grid>
						<Grid.Col span={12}>
							<NumberInput
								label={'Case Cost'}
								placeholder="Insert case cost..."
								prefix="£"
								decimalScale={2}
								thousandSeparator=","
								allowDecimal={true}
								allowNegative={false}
								key={form.key('caseCost')}
								{...form.getInputProps('caseCost')}
							/>
							<NumberInput
								label={'Case Quantity'}
								placeholder="Insert case quantity..."
								allowDecimal={false}
								thousandSeparator=","
								defaultValue={1_000_000}
								trimLeadingZeroesOnBlur={false}
								allowNegative={false}
								key={form.key('caseQuantity')}
								{...form.getInputProps('caseQuantity')}
							/>
						</Grid.Col>
					</Grid>
					<Space h={20} />
					<FormButtons close={cancelClose} />
				</form>
			</Modal>
		</>
	);
}
